import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Select, DatePicker, Switch, Button, Checkbox, Tag, Tabs, Table, Progress, message } from "antd";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import { regionOptions } from "../../helper/regions";
import { Section } from "../Employees/employeeUi";

const TOOLBAR = [[{ header: [1, 2, 3, false] }], ["bold", "italic", "underline", "strike"], [{ list: "ordered" }, { list: "bullet" }], ["blockquote", "link"], ["clean"]];
const STATUS_STYLE = { published: { color: "green", label: "Published" }, draft: { color: "default", label: "Draft" }, archived: { color: "orange", label: "Archived" } };

// One page for writing a new policy and for editing an existing one.
const PolicyForm = () => {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [policy, setPolicy] = useState(null);
  const [types, setTypes] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [acks, setAcks] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bump, setBump] = useState(false); // significant change: ask everyone to acknowledge again
  const REGIONS = useMemo(() => regionOptions(), []);
  const content = Form.useWatch("content", form) || "";
  const requiresAck = Form.useWatch("requiresAcknowledgement", form);
  const fnChosen = Form.useWatch("function", form);
  const typeOptions = types.filter((t) => !fnChosen || t.function === fnChosen).map((t) => ({ value: t._id, label: t.name }));

  useEffect(() => {
    request({ url: apiPath.policyTypes, method: "GET", onSuccess: (res) => { setTypes(res?.data?.types || []); setFunctions(res?.data?.functions || []); } });
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
    if (editing) {
      request({
        url: `${apiPath.policies}/${id}`,
        method: "GET",
        onSuccess: (res) => {
          const p = res?.data;
          setPolicy(p);
          form.setFieldsValue({ ...p, policyType: p.policyType?._id, departments: (p.departments || []).map((d) => d._id), effectiveDate: p.effectiveDate ? moment(p.effectiveDate) : null });
        },
        onError: () => message.error("Could not load this policy"),
      });
      request({ url: `${apiPath.policies}/${id}/acknowledgements`, method: "GET", onSuccess: (res) => setAcks(res?.data || null) });
    } else {
      form.setFieldsValue({ function: "HR", regions: [], departments: [], requiresAcknowledgement: false, content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onTypeChange = (typeId) => {
    const t = types.find((x) => x._id === typeId);
    if (t) form.setFieldsValue({ function: t.function }); // a type brings its usual function, still editable
  };

  // changing the function clears a type that belongs to another one
  const onFunctionChange = (fn) => {
    const t = types.find((x) => x._id === form.getFieldValue("policyType"));
    if (t && t.function !== fn) form.setFieldsValue({ policyType: undefined });
  };

  const payloadOf = (values) => ({
    title: values.title,
    policyType: values.policyType || null,
    function: values.function || "Company-wide",
    regions: values.regions || [],
    departments: values.departments || [],
    summary: values.summary || "",
    content: values.content || "",
    effectiveDate: values.effectiveDate ? values.effectiveDate.format("YYYY-MM-DD") : "",
    requiresAcknowledgement: !!values.requiresAcknowledgement,
    newVersion: editing && bump,
  });

  // Saves the policy; then, if asked, changes its status (publish / back to draft).
  const save = (nextStatus) =>
    form.validateFields().then((values) => {
      setSaving(true);
      const fail = (err) => { setSaving(false); message.error(err?.response?.data?.message || "Could not save"); };
      const changeStatus = (policyId) =>
        nextStatus
          ? request({ url: `${apiPath.policies}/${policyId}/status`, method: "PUT", data: { status: nextStatus }, onSuccess: () => done(policyId), onError: fail })
          : done(policyId);
      const done = (policyId) => {
        setSaving(false);
        message.success(nextStatus === "published" ? "Policy published" : "Policy saved");
        navigate(nextStatus || !editing ? "/policies" : `/policies/${policyId}`);
      };
      request({
        url: editing ? `${apiPath.policies}/${id}` : apiPath.policies,
        method: editing ? "PUT" : "POST",
        data: payloadOf(values),
        onSuccess: (res) => changeStatus(editing ? id : res?.data?._id),
        onError: fail,
      });
    }).catch(() => message.warning("Please fix the highlighted fields"));

  const status = policy?.status || "draft";
  const ackColumns = [
    { title: "Employee", dataIndex: "name", render: (v, r) => <div><b>{v}</b><div style={{ color: "#6B7280", fontSize: 12 }}>{r.employeeId}</div></div> },
    { title: "When", dataIndex: "acknowledgedAt", render: (v) => (v ? moment(v).format("DD MMM YYYY, HH:mm") : "Not yet") },
  ];

  return (
    <PageTransition>
      <div className="emp-form-bar">
        <div style={{ color: "#6B7280", fontSize: 13 }}>
          <Link to="/policies">Policies</Link> › <b style={{ color: "#111827" }}>{editing ? policy?.title || "Edit" : "New policy"}</b>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 style={{ fontSize: 22, margin: 0 }}>{editing ? "Edit policy" : "New policy"}</h1>
            {editing ? (
              <div style={{ color: "#6B7280" }}>
                Version {policy?.version} · <Tag color={STATUS_STYLE[status]?.color} style={{ margin: 0 }}>{STATUS_STYLE[status]?.label}</Tag>
              </div>
            ) : (
              <div style={{ color: "#6B7280" }}>Write the policy, choose who it applies to, then publish it.</div>
            )}
          </div>
          <Button onClick={() => navigate("/policies")}>Cancel</Button>
          <Button loading={saving} onClick={() => save(status === "published" ? null : "draft")}>{status === "published" ? "Save changes" : "Save draft"}</Button>
          <Button type="primary" loading={saving} onClick={() => save("published")}>{status === "published" ? "Save & keep published" : "Publish"}</Button>
        </div>
      </div>

      <Form layout="vertical" form={form} requiredMark>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)", gap: 16, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Section title="Policy">
              <Form.Item name="title" label="Title" rules={[{ required: true, message: "Enter a title" }]}>
                <Input placeholder="For example Work From Home Policy (Hong Kong)" />
              </Form.Item>
              <Form.Item name="policyType" label="Policy type" extra="The kinds of policy under the chosen function. You can add your own types from the Policies page.">
                <Select allowClear showSearch optionFilterProp="label" placeholder="Select a type" onChange={onTypeChange} options={typeOptions} />
              </Form.Item>
              <Form.Item name="summary" label="Short summary" extra="One or two lines shown in the list for employees.">
                <Input.TextArea rows={2} maxLength={600} showCount />
              </Form.Item>
              <Tabs
                items={[
                  {
                    key: "write",
                    label: "Write",
                    children: (
                      <Form.Item name="content" valuePropName="value" style={{ marginBottom: 0 }}>
                        <ReactQuill theme="snow" modules={{ toolbar: TOOLBAR }} style={{ background: "#fff" }} placeholder="Write the policy here: headings, lists, links…" />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "preview",
                    label: "Preview as an employee",
                    children: content.replace(/<[^>]*>/g, "").trim() ? (
                      <div className="policy-body" style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: 20, background: "#fff" }} dangerouslySetInnerHTML={{ __html: content }} />
                    ) : (
                      <div style={{ color: "#9CA3AF" }}>Nothing to preview yet.</div>
                    ),
                  },
                ]}
              />
            </Section>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Section title="Who it applies to">
              <Form.Item name="regions" label="Regions" extra="Leave empty for every region. Only employees in the chosen regions will see it.">
                <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="All regions" options={REGIONS} />
              </Form.Item>
              <Form.Item name="departments" label="Departments" extra="Leave empty for every department.">
                <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="All departments" options={departments.map((d) => ({ value: d._id, label: d.name }))} />
              </Form.Item>
              <Form.Item name="function" label="Function" extra="The business function this policy belongs to.">
                <Select onChange={onFunctionChange} options={functions.map((f) => ({ value: f, label: f }))} />
              </Form.Item>
            </Section>
            <Section title="Settings">
              <Form.Item name="effectiveDate" label="Effective from" getValueProps={(v) => ({ value: v })}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="requiresAcknowledgement" label="Employees must acknowledge" valuePropName="checked" extra="Each employee confirms they have read it. You can see who has not.">
                <Switch />
              </Form.Item>
              {editing && status === "published" ? (
                <Checkbox checked={bump} onChange={(e) => setBump(e.target.checked)}>
                  This is a significant change: make everyone acknowledge it again (becomes version {(policy?.version || 1) + 1})
                </Checkbox>
              ) : null}
            </Section>
            {editing && status === "published" && requiresAck && acks ? (
              <Section title="Acknowledgements" hint={`Version ${acks.version}`}>
                <Progress percent={acks.acknowledged.length + acks.pending.length ? Math.round((acks.acknowledged.length / (acks.acknowledged.length + acks.pending.length)) * 100) : 0} size="small" />
                <Tabs
                  size="small"
                  items={[
                    { key: "pending", label: `Not yet (${acks.pending.length})`, children: <Table rowKey="_id" size="small" columns={ackColumns.slice(0, 1)} dataSource={acks.pending} pagination={{ pageSize: 6, size: "small" }} /> },
                    { key: "done", label: `Done (${acks.acknowledged.length})`, children: <Table rowKey="_id" size="small" columns={ackColumns} dataSource={acks.acknowledged} pagination={{ pageSize: 6, size: "small" }} /> },
                  ]}
                />
              </Section>
            ) : null}
          </div>
        </div>
      </Form>
    </PageTransition>
  );
};

export default PolicyForm;
